import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Observable } from 'rxjs';
import { Permission } from '../../core/services/models/user';
import {
  Providers,
  RestApiService,
} from '../../core/services/rest-api-service';

export const profileGuard: CanActivateFn = (
  _route: ActivatedRouteSnapshot,
  _state: RouterStateSnapshot
):
  | Observable<boolean | UrlTree>
  | Promise<boolean | UrlTree>
  | boolean
  | UrlTree => {


  const router = inject(Router);
  const translateService = inject(TranslateService);

  const createProviderDetailsUrl = (me: any) => {
    if (me?.type === Permission.PROVIDER) {
      let destination: Providers = Providers.VET;
      switch (me.service.type) {
        case 'vet':
          destination = Providers.VET;
          break;
        case 'lodging':
          destination = Providers.ACCOMODATION;
          break;
        case 'beach':
          destination = Providers.DOG_FRIENDLY_BEACHES;
          break;
        case 'track':
          destination = Providers.DOG_PARKS;
          break;
        case 'restaurant':
          destination = Providers.RESTAURANT;
          break;
        case 'school':
          destination = Providers.SCHOOL;
          break;
        case 'wellness':
          destination = Providers.WELLNESS;
          break;
        case 'dog_photography':
          destination = Providers.DOG_PHOTOGRAPHY;
          break;
        case 'dog_cosmetics':
          destination = Providers.DOG_COSMETICS;
          break;
        case 'food_equipment':
          destination = Providers.FOOD_EQUIPMENT;
          break;
        case 'gift':
          destination = Providers.GIFT;
          break;
        case 'exhibit':
          destination = Providers.EXHIBIT;
          break;
        case 'funerary':
          destination = Providers.FUNERARY;
          break;
        case 'transport':
          destination = Providers.TRANSPORT;
          break;
        case 'pharmacy':
          destination = Providers.PHARMACY;
          break;
        case 'nonprofit':
          destination = Providers.NONPROFIT;
          break;
        case 'insurance':
          destination = Providers.INSURANCE;
          break;
      }
      return [
        '/',
        translateService.currentLang,
        'provider',
        destination,
        me.service.id,
      ];
    }
    return [];
  };

  const checkMe = (me: any) => {
    // checks if every required field of provider is filled out
    // returns wether the missing data is a service data or billing data
    // returns null when nothing is missing or its irrelevant
    const requiredAccountfields = ['type', 'name', 'email'];
    const requiredServiceFields = [
      'avatar_id',
      'address',
      'city',
      'country',
      'description',
      // 'email',
      'name',
      //'phone',
      'postal',
      'type',
    ];
    const requiredBillingFields = [
      'name',
      'country',
      'postal',
      'city',
      'address',
      'tax_number',
    ];

    let accountFieldsFilled = true;
    let serviceFieldsFilled = true;
    const timeFieldsFilled = true;

    let billingFieldsFilled = true;

    if (me && me.type === Permission.PROVIDER) {
      if (me?.service) {
        const foundEmptyAccountField = requiredAccountfields.find(
          k => me?.[k] === null || me?.[k] === undefined
        );
        accountFieldsFilled = !foundEmptyAccountField;

        const foundEmptyServiceField = requiredServiceFields.find(
          k => me.service?.[k] === null || me.service?.[k] === undefined
        );
        serviceFieldsFilled = !foundEmptyServiceField;

        // if (me.service?.time__0_24) {
        //   timeFieldsFilled = true;
        // } else {
        //   // has at least one day with _since and _until
        //   timeFieldsFilled = !!(
        //     (me.service?.monday_since && me.service?.monday_until) ||
        //     (me.service?.tuesday_since && me.service?.tuesday_until) ||
        //     (me.service?.wednesday_since && me.service?.wednesday_until) ||
        //     (me.service?.thursday_since && me.service?.thursday_until) ||
        //     (me.service?.friday_since && me.service?.friday_until) ||
        //     (me.service?.saturday_since && me.service?.saturday_until) ||
        //     (me.service?.sunday_since && me.service?.sunday_until)
        //   );
        // }

        if (me?.billing) {
          const foundEmptyBillingField = requiredBillingFields.find(
            k => me.billing?.[k] === null || me.billing?.[k] === undefined
          );
          billingFieldsFilled = !foundEmptyBillingField;
        } else {
          billingFieldsFilled = false;
        }

        if (accountFieldsFilled && serviceFieldsFilled && timeFieldsFilled) {
          if (billingFieldsFilled) {
            return null;
          } else {
            return 'billing_data_missing';
          }
        } else {
          return 'service_data_missing';
        }
      }
      return 'service_data_missing';
    }
    return null;
  };

  const permissionPromise = firstValueFrom(inject(RestApiService).getMe()).then(
    me => {
      if (!me) return false;

      switch (me.type) {
        case Permission.OWNER:
          return router.createUrlTree([
            '/',
            translateService.currentLang ?? 'en',
            'profile-owner',
          ]);
        case Permission.PROVIDER: {
          const dataIsMissing:
            | 'service_data_missing'
            | 'billing_data_missing'
            | null = checkMe(me);

          if (dataIsMissing) {
            if (dataIsMissing === 'service_data_missing') {
              // redirect to /profile-provider/provider-data with missingFields queryParam
              return router.createUrlTree(
                [
                  '/',
                  translateService.currentLang ?? 'en',
                  'profile-provider',
                  'provider-data',
                ],
                {
                  queryParams: { missingFields: 'true' },
                }
              );
            }
            if (dataIsMissing === 'billing_data_missing') {
              // redirect to /provider/prov-type/id with missingFields queryParam
              const url = createProviderDetailsUrl(me);
              return router.createUrlTree(url, {
                queryParams: { missingFields: 'true' },
              });
            }
          }

          /*return router.createUrlTree([
            '/',
            translateService.currentLang ?? 'en',
            'profile-provider',
            'provider-subscription',
          ]);*/
          return router.createUrlTree(createProviderDetailsUrl(me));
        }
        case Permission.ADMIN:
          return router.createUrlTree([
            '/',
            translateService.currentLang ?? 'en',
            'admin',
          ]);
        default:
          return false;
      }
    }
  );
  return permissionPromise;
};
